import React, { useState } from "react";
import { detectZipFromLocation, resolveMapboxToken } from "../utils/geolocateZip";

// A small "Use my location" control that sits next to a ZIP input. On click it asks the
// browser for the user's location, reverse-geocodes it to a ZIP, and hands the ZIP back
// via onZip. It owns its own busy/error state so pages only need to pass onZip. Renders
// nothing when no Mapbox token is configured (graceful no-op in token-less environments).
export default function UseMyLocationButton({ onZip, className = "" }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!resolveMapboxToken()) return null;

  const handleClick = async () => {
    setError("");
    setBusy(true);
    try {
      const zip = await detectZipFromLocation();
      if (onZip) onZip(zip);
    } catch (err) {
      setError(err && err.message ? err.message : "Could not get your location.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className}>
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        aria-label="Use my current location to fill in ZIP code"
        className="inline-flex items-center gap-1 text-sm font-medium text-blue-700 hover:text-blue-800 disabled:opacity-60 disabled:cursor-not-allowed dark:text-blue-300 dark:hover:text-blue-200"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4"
          aria-hidden="true"
        >
          <path d="M21 10c0 7-9 12-9 12s-9-5-9-12a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
        {busy ? "Locating..." : "Use my location"}
      </button>
      {error ? (
        <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
      ) : null}
    </div>
  );
}
